import React, { useCallback, useEffect, useState } from "react";
import TopNav from "../layouts/TopNav";
import "../Styles/invoiceTable.css";
import { ToastContainer, toast } from "react-toastify";
import TablePagination from "@mui/material/TablePagination";
import AdminPermission from "../components/Modals/PermissionModal.tsx/AdminPermission";
import { Link, useNavigate } from "react-router-dom";
import { BsSearch } from "react-icons/bs";
import ReLoginModal from "../components/ReLoginModal";
import { BiArrowBack } from "react-icons/bi";
import { CircularProgress } from "@mui/material";
import Swal from "sweetalert2";
import api from "../util/axios";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import SolarMenu from "../asset/svg/solarMenu";
import { SidebarModal } from "../components/Modals/sidebarModal";

interface Permission {
  view: boolean;
  add: boolean;
  edit: boolean;
  delete: boolean;
}

interface RolePermissions {
  [key: string]: Permission;
}

type Staff = {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
  city: string;
  country: string;
  state: string;
  account: { role: string; type: string, isActive: boolean }
};

type ApiError = {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
};

const AdminStaff = () => {
  const token = window.localStorage.getItem("token");
  const [getStaff, setGetStaff] = useState<Staff[]>([]);
  const [loading, setLoading] = useState(false);
  const [reLogin, setReLogin] = useState(false);
  const [permissions, setPermissions] = useState<RolePermissions | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchField, setSearchField] = useState("firstName");
  const [firstNameQuery, setFirstNameQuery] = useState("");
  const [lastNameQuery, setLastNameQuery] = useState("");
  const [emailQuery, setEmailQuery] = useState("");
  const [phoneQuery, setPhoneQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [modal, setModal] = useState(false);
  const [modalDisplay, setModalDisplay] = useState(false);
  const [uniqueRoles, setUniqueRoles] = useState<string[]>([]);
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [selectedStaffId, setSelectedStaffId] = useState<string | null>(null);
  const navigate = useNavigate();
  const [formData, setFormData] = useState<{
    staffId: string,
    firstName: string,
    lastName: string,
    email: string,
    phone: string,
    role: string,
    city: string,
    country: string,
    state: string,
  }>({
    staffId: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    role: "",
    city: "",
    country: "",
    state: "",
  });

  const userRole = window.localStorage.getItem("userRole");
  const isAdmin = userRole && userRole.toLowerCase() === "admin";
  const isDisabled = !isAdmin || loading;

  function fetchStaff() {
    setLoading(true);
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return api
      .get("/staff", {
        headers: headers,
      })
      .then((res) => {
        setGetStaff(res.data.data);
        setLoading(false);
      })
      .catch((error) => {
        if (
          error.response.data.message === "jwt expired" ||
          error.response.data.message === "jwt malformed"
        ) {
          setReLogin(true);
        } else {
          setReLogin(false);
        }
        setLoading(false);
        toast.error(error.response.data.message || "An error occurred");
      });
  }

  const fetchRoles = () => {
    return api
      .get("/roles", {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        const userRole = res.data.data[0].permissions;
        setPermissions(userRole);
      })
      .catch((err) => {
        Swal.fire({
          title: "Error!",
          text: "Failed to fetch user permissions.",
          icon: "error",
          confirmButtonColor: "red",
        });
      });
  };

  useEffect(() => {
    fetchRoles();
    fetchStaff();
  }, []);

  useEffect(() => {
    if (selectedStaffId && modalDisplay) {
      const staff = getStaff.find((staff) => staff._id === selectedStaffId);
      if (staff) {
        setFormData({
          staffId: staff._id || "",
          firstName: staff.firstName || "N/A",
          lastName: staff.lastName || "N/A",
          city: staff.city || "N/A",
          email: staff.email || "N/A",
          phone: staff.phone || "N/A",
          state: staff.state || "N/A",
          country: staff.country || "N/A",
          role: staff.account.role || "N/A"
        });
      } else {
        toast.error("Selected staff not found. Please try again.");
        setModalDisplay(false);
      }
    }
  }, [selectedStaffId, modalDisplay, getStaff]);

  useEffect(() => {
    if (getStaff.length > 0) {
      const roles = [...new Set(getStaff.map((staff) => staff.account.role))];
      setUniqueRoles(roles.sort());
    }
  }, [getStaff]);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const openModal = () => {
    setModal(!modal);
  };

  const filteredStaff = getStaff.filter((staff: Staff) => {
    // Apply role filter if a role is selected
    if (selectedRole && staff.account.role !== selectedRole) {
      return false;
    }

    // Apply search filters
    if (searchField !== "combined" && !searchQuery) return true;
    if (
      searchField === "combined" &&
      !firstNameQuery &&
      !lastNameQuery &&
      !emailQuery &&
      !phoneQuery
    )
      return true;

    const firstName = staff.firstName?.toLowerCase() || "";
    const lastName = staff.lastName?.toLowerCase() || "";
    const email = staff.email?.toLowerCase() || "";
    const phone = staff.phone?.toLowerCase() || "";

    const firstNameMatch =
      searchField === "combined"
        ? firstName.includes(firstNameQuery.toLowerCase())
        : firstName.includes(searchQuery.toLowerCase());
    const lastNameMatch =
      searchField === "combined"
        ? lastName.includes(lastNameQuery.toLowerCase())
        : lastName.includes(searchQuery.toLowerCase());
    const emailMatch =
      searchField === "combined"
        ? email.includes(emailQuery.toLowerCase())
        : email.includes(searchQuery.toLowerCase());
    const phoneMatch =
      searchField === "combined"
        ? phone.includes(phoneQuery.toLowerCase())
        : phone.includes(searchQuery.toLowerCase());

    switch (searchField) {
      case "firstName":
        return firstNameMatch;
      case "lastName":
        return lastNameMatch;
      case "email":
        return emailMatch;
      case "phone":
        return phoneMatch;
      case "combined":
        return firstNameMatch && lastNameMatch && emailMatch && phoneMatch;
      default:
        return firstNameMatch;
    }
  });

  const handleEditStaff = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.staffId) {
      toast.error("No staff selected for edit!");
      return;
    }
    const token = window.localStorage.getItem("token");
    if (!token) {
      setReLogin(true);
      return;
    }
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    setLoading(true);
    try {
      const response = await api.patch(`/staff/${formData.staffId}`,
        {
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          email: formData.email,
          city: formData.city,
          state: formData.state,
          country: formData.country
        },
        { headers }
      );
      toast.success("Staff Edited Successfully!");
      setModalDisplay(false);
      fetchStaff();
    } catch (error) {
      const err = error as ApiError;
      const errorMessage =
        err.response?.data?.message || err.message || "Failed to update staff details";
      if (
        errorMessage.includes("jwt expired") ||
        errorMessage.includes("jwt malformed")
      ) {
        setReLogin(true);
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [formData])

  return (
    <div className="board">
      <ToastContainer />
      <TopNav title="Staff list" />
      {reLogin ? <ReLoginModal /> : null}
      <div className="userPro">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center cursor-pointer bg-red-400 hover:bg-red-500 text-white gap-2 py-1 px-10 rounded-lg"
        >
          <BiArrowBack /> Back
        </button>
        <div
          className="method"
          style={{ justifyContent: "flex-end", marginTop: "15px" }}
        >
          <div className="searchFieldSelect">
            <label htmlFor="filter">Search filter: </label>
            <select
              id="filter"
              value={searchField}
              onChange={(e) => setSearchField(e.target.value)}
            >
              <option value="firstName">First Name</option>
              <option value="lastName">Last Name</option>
              <option value="email">Email</option>
              <option value="phone">Phone</option>
              <option value="combined">Mixed search</option>
            </select>
          </div>
          {searchField === "combined" ? (
            <div className="combinedSearch">
              <input
                type="text"
                placeholder="First Name"
                value={firstNameQuery}
                onChange={(e) => setFirstNameQuery(e.target.value)}
              />
              <input
                type="text"
                placeholder="Last Name"
                value={lastNameQuery}
                onChange={(e) => setLastNameQuery(e.target.value)}
              />
              <input
                type="text"
                placeholder="Email"
                value={emailQuery}
                onChange={(e) => setEmailQuery(e.target.value)}
              />
              <input
                type="text"
                placeholder="Phone"
                value={phoneQuery}
                onChange={(e) => setPhoneQuery(e.target.value)}
              />
            </div>
          ) : (
            <div className="apInput">
              <input
                type="text"
                value={searchQuery}
                placeholder="Search"
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <BsSearch className="apIcon" />
            </div>
          )}
        </div>

        <div className="invoiceTable">
          {loading ? (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <CircularProgress />
              <h2>Please wait while we process the request...</h2>
            </div>
          ) : (
            <table className="inTable">
              <thead>
                <tr className="heTble tabHead">
                  <th>First Name</th>
                  <th>Last Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredStaff.length > 0 ? (
                  filteredStaff
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((staff, inx) => {
                      const active = staff?.account.isActive.toString();
                      return (
                        <tr key={inx} className="taData">
                          <td>{staff?.firstName}</td>
                          <td>{staff?.lastName}</td>
                          <td>{staff?.email}</td>
                          <td>{staff?.phone}</td>
                          <td>{staff?.account.role}</td>
                          <td>{active.toLowerCase() === "true" ? "Active" : "Inactive"}</td>
                          <td className="!text-center">
                            <DropdownMenu.Root>
                              <DropdownMenu.Trigger asChild>
                                <button className="">
                                  <SolarMenu />
                                </button>
                              </DropdownMenu.Trigger>

                              <DropdownMenu.Portal>
                                <DropdownMenu.Content
                                  className="bg-white rounded shadow p-2"
                                  sideOffset={5}
                                >
                                  <DropdownMenu.Item
                                    className="px-2 text-blue-500 py-2 rounded hover:bg-gray-100 focus:outline-none cursor-pointer"
                                    onSelect={() => {
                                      setSelectedStaffId(staff._id);
                                      setModalDisplay(true);
                                    }}
                                  >
                                    View / Edit
                                  </DropdownMenu.Item>
                                </DropdownMenu.Content>
                              </DropdownMenu.Portal>
                            </DropdownMenu.Root>
                          </td>
                        </tr>
                      );
                    })
                ) : (
                  <tr>
                    <td colSpan={6} style={{ textAlign: "center" }}>
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
        <SidebarModal
          title="View / Edit Staff"
          display={modalDisplay}
          close={() => {
            setModalDisplay(false);
          }}
        >
          <div className="invoice-modal-content" style={{ padding: "20px" }}>
            <form onSubmit={handleEditStaff}>
              <div style={{ marginBottom: "15px" }}>
                <label style={{ display: "block", marginBottom: "5px" }}>
                  First Name
                </label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      firstName: e.target.value,
                    }))
                  }
                  style={{
                    width: "100%",
                    padding: "8px",
                    backgroundColor: "#fff",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    outline: "none",
                  }}
                />
              </div>
              <div style={{ marginBottom: "15px" }}>
                <label style={{ display: "block", marginBottom: "5px" }}>
                  Last Name
                </label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      lastName: e.target.value,
                    }))
                  }
                  style={{
                    width: "100%",
                    padding: "8px",
                    backgroundColor: "#fff",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    outline: "none",
                  }}
                />
              </div>
              <div style={{ marginBottom: "15px" }}>
                <label style={{ display: "block", marginBottom: "5px" }}>
                  Email
                </label>
                <input
                  type="text"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      email: e.target.value,
                    }))
                  }
                  style={{
                    width: "100%",
                    padding: "8px",
                    backgroundColor: "#fff",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    outline: "none",
                  }}
                />
              </div>
              <div style={{ marginBottom: "15px" }}>
                <label style={{ display: "block", marginBottom: "5px" }}>
                  Phone
                </label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      phone: e.target.value,
                    }))
                  }
                  style={{
                    width: "100%",
                    padding: "8px",
                    backgroundColor: "#fff",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    outline: "none",
                  }}
                />
              </div>
              <div style={{ marginBottom: "15px" }}>
                <label style={{ display: "block", marginBottom: "5px" }}>
                  Role
                </label>
                <input
                  type="text"
                  value={formData.role}
                  readOnly
                  style={{
                    width: "100%",
                    padding: "8px",
                    backgroundColor: "#f0f0f0",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    cursor: "not-allowed",
                    outline: "none",
                  }}
                />
              </div>
              <div style={{ marginBottom: "15px" }}>
                <label style={{ display: "block", marginBottom: "5px" }}>
                  City
                </label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      city: e.target.value,
                    }))
                  }
                  style={{
                    width: "100%",
                    padding: "8px",
                    backgroundColor: "#fff",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    outline: "none",
                  }}
                />
              </div>
              <div style={{ marginBottom: "15px" }}>
                <label style={{ display: "block", marginBottom: "5px" }}>
                  State
                </label>
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      state: e.target.value,
                    }))
                  }
                  style={{
                    width: "100%",
                    padding: "8px",
                    backgroundColor: "#fff",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    outline: "none",
                  }}
                />
              </div>
              <div style={{ display: "flex", gap: "10px" }}>
                <button
                  type="submit"
                  disabled={isDisabled}
                  style={{
                    padding: "10px 20px",
                    backgroundColor: loading ? "#0B77C5" : "#0B77C5",
                    color: "#fff",
                    border: "none",
                    width: "50%",
                    borderRadius: "4px",
                    cursor: isDisabled ? "not-allowed" : "pointer",
                  }}
                >
                  {loading ? <CircularProgress size={20} style={{ color: "#fff" }} /> : "Edit"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setModalDisplay(false);
                    setFormData({
                      staffId: "",
                      firstName: "",
                      lastName: "",
                      email: "",
                      phone: "",
                      role: "",
                      city: "",
                      country: "",
                      state: "",
                    });
                  }}
                  style={{
                    padding: "10px 20px",
                    backgroundColor: "#ccc",
                    color: "#000",
                    width: "50%",
                    border: "none",
                    borderRadius: "4px",
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </SidebarModal>
        <TablePagination
          rowsPerPageOptions={[15, 25, 50]}
          component="div"
          count={filteredStaff.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
        {modal ? <AdminPermission openModal={openModal} /> : null}
      </div>
      <div className="roleFilter" style={{ marginTop: "15px", marginLeft: "30px", display: "flex", alignItems: "center", gap: "10px" }}>
        <p>Filter by Role: </p>
        <button
          onClick={() => setSelectedRole(null)}
          style={{
            padding: "5px 10px",
            backgroundColor: selectedRole === null ? "#0B77C5" : "#ccc",
            color: selectedRole === null ? "#fff" : "#000",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          All
        </button>
        {uniqueRoles.map((role) => (
          <button
            key={role}
            onClick={() => setSelectedRole(role)}
            style={{
              padding: "5px 10px",
              backgroundColor: selectedRole === role ? "#0B77C5" : "#ccc",
              color: selectedRole === role ? "#fff" : "#000",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            {role}
          </button>
        ))}
      </div>
    </div>
  );
};

export default AdminStaff;